import type { CSSProperties } from "react";

/**
 * Per-option color rendering for SINGLE_SELECT / MULTI_SELECT custom fields.
 *
 * Colors live in the additive `Field.optionColors` map (label -> hex). A chip
 * is drawn as a soft tint of the user-picked hex over the theme surface, with
 * the hex itself (pulled toward the theme font color) as text — same idea as
 * the fixed `chip-*-bg/fg` slotting chips, generalised to an arbitrary hex.
 * The `color-mix` targets are theme tokens, so chips stay readable in light
 * and dark mode without per-color tuning.
 */

/** 6-digit hex, mirrors backend AdditionalFieldConstraints.OPTION_COLOR_PATTERN. */
const HEX_PATTERN = /^#[0-9a-fA-F]{6}$/;

/** A `var(--token)` reference — the form of the default chart palette tokens. */
const CSS_VAR_PATTERN = /^var\(--[\w-]+\)$/;

/**
 * Strict 6-digit-hex check. This is the PERSISTENCE/validation gate — only hex
 * is ever stored in `Field.optionColors` (mirrors the backend pattern). Do NOT
 * loosen it; render-time code that also needs to accept the default palette's
 * CSS vars uses `isRenderableOptionColor` instead.
 */
export function isValidOptionColor(hex?: string | null): hex is string {
  return typeof hex === "string" && HEX_PATTERN.test(hex);
}

/**
 * Render-time check: a color we can actually paint a chip with — either a stored
 * hex OR a `var(--color-chart-*)` token from the default palette fallback
 * (`defaultChartColors`). `color-mix` and a raw `backgroundColor` both accept a
 * var operand, so chips and dropdown dots render the same theme-adaptive colors
 * the editor swatches and dashboard charts already show.
 */
export function isRenderableOptionColor(color?: string | null): color is string {
  return (
    isValidOptionColor(color) ||
    (typeof color === "string" && CSS_VAR_PATTERN.test(color))
  );
}

/**
 * Inline style for an option chip. Returns `{}` for a missing / invalid color
 * so callers fall back to their existing neutral rendering.
 */
export function optionChipStyle(hex?: string | null): CSSProperties {
  if (!isRenderableOptionColor(hex)) return {};
  return {
    backgroundColor: `color-mix(in srgb, ${hex} 15%, var(--color-surface-primary))`,
    color: `color-mix(in srgb, ${hex} 72%, var(--color-font-primary))`,
    borderColor: `color-mix(in srgb, ${hex} 30%, transparent)`,
  };
}

type OptionChipProps = {
  label: string;
  /** Hex color from `Field.optionColors`. Absent / invalid => neutral chip. */
  color?: string | null;
  className?: string;
};

/**
 * A single colored option chip. Used for SINGLE_SELECT / MULTI_SELECT values
 * in table cells, guest detail panels and filter summaries. A colorless
 * option renders as a subtle neutral chip.
 */
export const OptionChip: React.FC<OptionChipProps> = ({
  label,
  color,
  className,
}) => {
  const colored = isRenderableOptionColor(color);
  return (
    <span
      className={`inline-flex w-max max-w-full items-center truncate rounded-md border px-2 py-0.5 body-3-regular ${
        colored ? "" : "surface-secondary outline-primary font-secondary"
      } ${className ?? ""}`}
      style={colored ? optionChipStyle(color) : undefined}
    >
      {label}
    </span>
  );
};
